import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';

// Brand Logo Component with multiple design variations
const BrandLogo = ({ variant = 'command', className = '' }) => {
  const { isDark } = useTheme();
  const [showCursor, setShowCursor] = useState(true);

  // Cursor blink effect - slower, more subtle
  useEffect(() => {
    const interval = setInterval(() => {
      setShowCursor((prev) => !prev);
    }, 1000); // Changed from 530ms to 1000ms (1 second)
    return () => clearInterval(interval);
  }, []);

  // Variant 1: Command Prompt Style - > zunayed_
  const CommandPrompt = () => (
    <div className={`font-mono font-semibold text-2xl md:text-3xl flex items-center ${className}`}>
      <span className={`${isDark ? 'text-dark-brand-primary' : 'text-light-brand-primary'} mr-1`}>
        &gt;
      </span>
      <span className={isDark ? 'text-dark-text-primary' : 'text-light-text-primary'}>
        zunayed
      </span>
      <span
        className={`${isDark ? 'text-dark-brand-primary' : 'text-light-brand-primary'} ${
          showCursor ? 'opacity-100' : 'opacity-0'
        } transition-opacity duration-200`}
      >
        _
      </span>
    </div>
  );

  // Variant 2: JSX Tag Style - <ZF/>
  const JSXTag = () => (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
      className={`font-mono font-bold text-2xl md:text-3xl ${className}`}
    >
      <span className={isDark ? 'text-dark-brand-secondary' : 'text-light-brand-secondary'}>
        &lt;
      </span>
      <span className={isDark ? 'text-dark-text-primary' : 'text-light-text-primary'}>
        ZF
      </span>
      <span className={isDark ? 'text-dark-brand-secondary' : 'text-light-brand-secondary'}>
        /&gt;
      </span>
    </motion.div>
  );

  // Variant 3: Function Call Style - ZF()
  const FunctionCall = () => (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={`font-mono font-bold text-2xl md:text-3xl ${className}`}
    >
      <span className={isDark ? 'text-dark-brand-accent' : 'text-light-brand-accent'}>
        ZF
      </span>
      <span className={isDark ? 'text-dark-text-secondary' : 'text-light-text-secondary'}>
        ()
      </span>
    </motion.div>
  );

  // Variant 4: Terminal Window Style - [zunayed@dev]
  const TerminalWindow = () => (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5 }}
      className={`font-mono font-medium text-lg md:text-xl flex items-center ${className}`}
    >
      <span className={isDark ? 'text-dark-text-secondary' : 'text-light-text-secondary'}>
        [
      </span>
      <span className={isDark ? 'text-dark-brand-primary' : 'text-light-brand-primary'}>
        zunayed
      </span>
      <span className={isDark ? 'text-dark-text-secondary' : 'text-light-text-secondary'}>
        @
      </span>
      <span className={isDark ? 'text-dark-brand-secondary' : 'text-light-brand-secondary'}>
        dev
      </span>
      <span className={isDark ? 'text-dark-text-secondary' : 'text-light-text-secondary'}>
        ]
      </span>
    </motion.div>
  );

  // Variant 5: Hex Code Style - 0xZF
  const HexCode = () => (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
      className={`font-mono font-bold text-2xl md:text-3xl ${className}`}
    >
      <span className={isDark ? 'text-dark-brand-secondary' : 'text-light-brand-secondary'}>
        0x
      </span>
      <span className={isDark ? 'text-dark-text-primary' : 'text-light-text-primary'}>
        ZF
      </span>
    </motion.div>
  );

  // Variant 6: File Extension Style - zunayed.dev
  const FileExtension = () => (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5 }}
      className={`font-mono font-semibold text-xl md:text-2xl ${className}`}
    >
      <span className={isDark ? 'text-dark-text-primary' : 'text-light-text-primary'}>
        zunayed
      </span>
      <span className={isDark ? 'text-dark-brand-primary' : 'text-light-brand-primary'}>
        .dev
      </span>
    </motion.div>
  );

  // Select variant
  const variants = {
    command: <CommandPrompt />,
    jsx: <JSXTag />,
    function: <FunctionCall />,
    terminal: <TerminalWindow />,
    hex: <HexCode />,
    file: <FileExtension />,
  };

  return variants[variant] || variants.command;
};

export default BrandLogo;